// Download Premium Handler
// Manages download button restrictions for non-premium users

import { subscriptionManager } from './subscription.js';
import { trackCheckoutClick } from './analytics.js';
import { getCheckoutUrl, PRODUCT_ANNUAL } from './checkout-urls.js';

class DownloadPremiumManager {
    constructor() {
        this.downloadButtons = [];
        this.modal = null;
        this.subscriptionState = {
            isLoggedIn: false,
            isPremium: false
        };
        
        this.init();
    }

    init() {
        console.log('🔒 Initializing Download Premium Manager...');
        
        // Setup DOM elements
        this.setupElements();
        
        // Create premium modal
        this.createPremiumModal();
        
        // Setup subscription observer
        this.setupSubscriptionObserver();
        
        console.log('✅ Download Premium Manager initialized');
    }

    setupElements() {
        console.log('🔍 Setting up download premium elements...');
        
        // Find all download buttons
        this.downloadButtons = [
            {
                element: document.getElementById('download-btn'),
                name: 'Download PDF',
                type: 'pdf'
            },
            {
                element: document.getElementById('download-pdf-bed'),
                name: 'Download PDF',
                type: 'pdf'
            },
            {
                element: document.getElementById('download-garden-plan'),
                name: 'Download Garden Plan',
                type: 'garden-plan'
            }
        ];

        // Find calendar download buttons (they are created dynamically)
        this.setupCalendarDownloadObserver();

        // Filter out null elements and setup click handlers
        this.downloadButtons = this.downloadButtons.filter(button => {
            if (button.element) {
                this.setupDownloadClickHandler(button);
                return true;
            }
            return false;
        });

        console.log(`📊 Found ${this.downloadButtons.length} download buttons`);
    }

    setupCalendarDownloadObserver() {
        // Use MutationObserver to detect when calendar download buttons are added
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if the added node or its descendants contain calendar download buttons
                        const calendarDownloadBtns = node.querySelectorAll ? 
                            node.querySelectorAll('.download-btn') : [];
                        
                        calendarDownloadBtns.forEach((btn) => {
                            // Check if this is a calendar download button and not already tracked
                            if (btn.textContent.includes('Download Calendar') && 
                                !this.downloadButtons.some(tracked => tracked.element === btn)) {
                                
                                const calendarButton = {
                                    element: btn,
                                    name: 'Download Calendar',
                                    type: 'calendar'
                                };
                                
                                this.downloadButtons.push(calendarButton);
                                this.setupDownloadClickHandler(calendarButton);
                                console.log('📅 Calendar download button detected and tracked');
                            }
                        });
                    }
                });
            });
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    setupDownloadClickHandler(button) {
        if (!button.element) return;
        // Downloads are allowed for all users; no premium gate or modal
        console.log(`🔗 Click handler setup for ${button.name}`);
    }

    createPremiumModal() {
        console.log('🎨 Creating premium download modal...');
        
        // Create modal HTML
        const modal = document.createElement('div');
        modal.className = 'download-premium-modal';
        modal.innerHTML = `
            <div class="download-premium-overlay">
                <div class="download-premium-content">
                    <button class="download-premium-close" aria-label="Close modal">
                        <i class="fa fa-times"></i>
                    </button>
                    
                    <div class="download-premium-icon">
                        <i class="fa fa-download"></i>
                    </div>
                    
                    <h2>Premium Feature</h2>
                    
                    <p class="download-premium-message">
                        Download functionality is available for Premium users only, Please Sign in first to continue.
                    </p>
                    
                    <div class="download-premium-features">
                        <h3>Premium Download Features:</h3>
                        <ul>
                            <li><i class="fa fa-check"></i> Download garden layouts as PDF</li>
                            <li><i class="fa fa-check"></i> Export planting calendars</li>
                            <li><i class="fa fa-check"></i> Save garden plans as images</li>
                            <li><i class="fa fa-check"></i> Unlimited downloads</li>
                        </ul>
                    </div>
                    
                    <div class="download-premium-buttons">
                        <button class="auth-cta-btn" id="download-premium-upgrade">
                            <i class="fa fa-star"></i>
                            Upgrade to Premium
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Add styles
        const style = document.createElement('style');
        style.textContent = `
            .download-premium-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.8);
                display: none;
                justify-content: center;
                align-items: center;
                z-index: 9999;
                backdrop-filter: blur(5px);
            }

            .download-premium-modal.visible {
                display: flex;
            }

            .download-premium-overlay {
                background: rgba(0, 0, 0, 0.5);
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 20px;
            }

            .download-premium-content {
                background: white;
                border-radius: 16px;
                padding: 30px;
                max-width: 450px;
                width: 100%;
                text-align: center;
                position: relative;
                box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
                border: 2px solid rgba(76, 175, 80, 0.2);
            }

            .download-premium-close {
                position: absolute;
                top: 15px;
                right: 15px;
                background: none;
                border: none;
                font-size: 20px;
                color: #666;
                cursor: pointer;
                padding: 8px;
                border-radius: 50%;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s ease;
            }

            .download-premium-close:hover {
                background: #f0f0f0;
                color: #333;
            }

            .download-premium-icon {
                font-size: 48px;
                color: #4CAF50;
                margin-bottom: 20px;
            }

            .download-premium-content h2 {
                color: #2c5530;
                font-size: 28px;
                margin-bottom: 15px;
                font-weight: bold;
            }

            .download-premium-message {
                color: #555;
                font-size: 16px;
                margin-bottom: 25px;
                line-height: 1.5;
            }

            .download-premium-features {
                background: #f8f9fa;
                border-radius: 12px;
                padding: 20px;
                margin-bottom: 25px;
                text-align: left;
            }

            .download-premium-features h3 {
                color: #2c5530;
                font-size: 18px;
                margin-bottom: 15px;
                text-align: center;
            }

            .download-premium-features ul {
                list-style: none;
                padding: 0;
                margin: 0;
            }

            .download-premium-features li {
                display: flex;
                align-items: center;
                margin-bottom: 10px;
                color: #555;
                font-size: 14px;
            }

            .download-premium-features li:last-child {
                margin-bottom: 0;
            }

            .download-premium-features li i {
                color: #4CAF50;
                margin-right: 10px;
                font-size: 16px;
            }

            .download-premium-buttons {
                display: flex;
                justify-content: center;
            }

            .download-premium-buttons .auth-cta-btn {
                background: #4CAF50;
                color: white;
                border: none;
                padding: 15px 25px;
                border-radius: 8px;
                font-weight: bold;
                cursor: pointer;
                transition: all 0.3s ease;
                font-size: 16px;
                box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
            }

            .download-premium-buttons .auth-cta-btn:hover {
                background: #45a049;
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
            }

            .download-premium-buttons .auth-cta-btn i {
                margin-right: 8px;
            }

            /* Responsive design */
            @media (max-width: 768px) {
                .download-premium-content {
                    max-width: 350px;
                    padding: 25px 20px;
                }

                .download-premium-icon {
                    font-size: 40px;
                }

                .download-premium-content h2 {
                    font-size: 24px;
                }

                .download-premium-message {
                    font-size: 14px;
                }

                .download-premium-features h3 {
                    font-size: 16px;
                }

                .download-premium-features li {
                    font-size: 13px;
                }

                .download-premium-buttons .auth-cta-btn {
                    padding: 12px 20px;
                    font-size: 14px;
                }
            }

            @media (max-width: 480px) {
                .download-premium-content {
                    max-width: 300px;
                    padding: 20px 15px;
                }

                .download-premium-icon {
                    font-size: 36px;
                }

                .download-premium-content h2 {
                    font-size: 20px;
                }

                .download-premium-message {
                    font-size: 13px;
                }

                .download-premium-features {
                    padding: 15px;
                }

                .download-premium-features h3 {
                    font-size: 15px;
                }

                .download-premium-features li {
                    font-size: 12px;
                }

                .download-premium-buttons .auth-cta-btn {
                    padding: 10px 18px;
                    font-size: 13px;
                }
            }
        `;

        // Append style and modal to document
        document.head.appendChild(style);
        document.body.appendChild(modal);

        this.modal = modal;

        // Setup event listeners
        this.setupModalEventListeners();

        console.log('✅ Premium download modal created');
    }

    setupModalEventListeners() {
        if (!this.modal) return;

        // Close button
        const closeBtn = this.modal.querySelector('.download-premium-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.hideModal();
            });
        }

        // Click outside to close
        const overlay = this.modal.querySelector('.download-premium-overlay');
        if (overlay) {
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    this.hideModal();
                }
            });
        }

        // Upgrade button
        const upgradeBtn = this.modal.querySelector('#download-premium-upgrade');
        if (upgradeBtn) {
            upgradeBtn.addEventListener('click', () => {
                this.handleUpgradeClick();
            });
        }

        // ESC key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('visible')) {
                this.hideModal();
            }
        });
    }

    setupSubscriptionObserver() {
        console.log('👂 Setting up download premium subscription observer...');
        
        subscriptionManager.onSubscriptionChange((subscriptionState) => {
            console.log('🔄 Download premium subscription state change:', subscriptionState);
            this.subscriptionState = subscriptionState;
        });
    }

    showPremiumModal(featureName = 'Download') {
        if (!this.modal) return;

        console.log(`🔒 Showing premium modal for ${featureName}`);
        
        // Update message with specific feature name
        const message = this.modal.querySelector('.download-premium-message');
        if (message) {
            message.innerHTML = `${featureName} is available for Premium users only,<br><b>Please Sign in first to continue.</b>`;
        }

        // Show modal
        this.modal.classList.add('visible');
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    hideModal() {
        if (!this.modal) return;

        console.log('🔒 Hiding premium modal');
        this.modal.classList.remove('visible');
        document.body.style.overflow = ''; // Restore scrolling
    }

    handleUpgradeClick() {
        console.log('💳 Upgrade to premium clicked from download modal');
        
        const { isLoggedIn } = this.subscriptionState;
        
        if (!isLoggedIn) {
            console.log('🔑 User not logged in — opening checkout in new tab');
            this.hideModal();
            trackCheckoutClick(PRODUCT_ANNUAL, 'download_modal');
            const tab = window.open(getCheckoutUrl(PRODUCT_ANNUAL, subscriptionManager.currentUser), '_blank');
            if (tab) tab.focus();
        } else {
            // User logged in but not premium - trigger upgrade
            console.log('💳 Triggering upgrade process...');
            this.hideModal();
            
            // Try to trigger existing upgrade functionality
            if (window.plantAuthManager && window.plantAuthManager.handleUpgradeClick) {
                window.plantAuthManager.handleUpgradeClick();
            } else {
                // Fallback: simulate upgrade for now
                this.simulateUpgrade();
            }
        }
    }

    async simulateUpgrade() {
        console.log('🔄 Simulating premium upgrade...');
        
        try {
            const result = await subscriptionManager.upgradeToPremium();
            if (result.success) {
                console.log('✅ Upgrade successful!');
                alert('🎉 Welcome to Premium! You now have access to all download features.');
            } else {
                console.error('❌ Upgrade failed:', result.error);
                alert('❌ Upgrade failed. Please try again.');
            }
        } catch (error) {
            console.error('❌ Error during upgrade:', error);
            alert('❌ An error occurred during upgrade. Please try again.');
        }
    }

    // Public method to check if downloads are allowed
    isDownloadAllowed() {
        return this.subscriptionState.isPremium;
    }

    // Public method to get subscription state
    getSubscriptionState() {
        return this.subscriptionState;
    }
}

// Create and export singleton instance
export const downloadPremiumManager = new DownloadPremiumManager();

// Export initialization function
export const initializeDownloadPremium = () => {
    console.log('🚀 Download Premium system initialized');
    
    // Make available globally for debugging
    window.downloadPremiumManager = downloadPremiumManager;
    
    console.log('💡 Run window.downloadPremiumManager.getSubscriptionState() to check status');
    
    return downloadPremiumManager;
}; 
